import React, { Component } from 'react';
import {
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    TextField,
    withStyles
} from '@material-ui/core';
import CustomButton from "../common/customButton";
import FlatList from "../common/flatList";

const styles = theme => ({
    sectionBody: {
        padding: '1rem',
        position: 'relative',
    },
    actionButtons: {
        position: 'absolute',
        top: 10,
        right: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    clientActionButtons: {
        marginTop: '1rem',
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
    },
    bodyText: {
        fontSize: '1rem',
        color: '#212529',
        marginBottom: '1rem',
    },
    recebimentoButton: {
        backgroundColor: '#257b79',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#1a5a58',
        },
    },
    respostaBox: {
        backgroundColor: 'rgba(0, 8, 255, 0.1)',
        padding: '0.625rem',
        marginTop: '1rem',
        marginBottom: '1rem',
    },
    recusaBox: {
        backgroundColor: 'rgba(255, 0, 0, 0.1)',
        padding: '0.625rem',
        marginTop: '1rem',
        marginBottom: '1rem',
    },
    adminResponseActions: {
        marginTop: '0.75rem',
    },
});

/**
 * DetailsEstimativaSection - Shared component for displaying estimativa details
 * 
 * Props:
 * - estimativa: object - The estimativa data
 * - modo: string - "cliente" | "workshop" | "admin" - Controls which buttons are shown
 * - rotasimplesempresa: string - Company name (for workshop/admin)
 * 
 * Workshop/Admin callbacks:
 * - onReceberEquipamento(id): Called when "Receber Equipamento" is clicked
 * - onCadastrarEquipamento(): Called when "Cadastrar Equipamento" is clicked
 * - onFazerApontamento(userId, empresa, orcamentoId, estimativaId): Called when "Fazer Apontamento" is clicked
 * 
 * Client callbacks:
 * - onAceitarEstimativa(id): Called when "Aceitar" is clicked (opens modal)
 * - onRecusarEstimativa(id): Called when "Recusar" or "Cancelar e Recusar" is clicked
 * - onVerDetalhesEnvio(id): Called when "Ver detalhes de envio" is clicked
 */
class DetailsEstimativaSection extends Component {
    formatarMoeda = (valor) => {
        if (valor == null || valor === '') return '-';
        return Number(valor).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
    };

    // Workshop/Admin buttons: equipment workflow
    renderWorkshopAdminButtons = () => {
        const {
            classes,
            estimativa,
            rotasimplesempresa,
            onReceberEquipamento,
            onCadastrarEquipamento,
            onFazerApontamento,
            onApproveMaintenance,
            onMarkAsSent,
            onFinalizarFluxo
        } = this.props;
        const status = estimativa?.status?.toLowerCase();

        if (status === 'equipamento em analise' || status === 'equipamento em análise') {
            return (
                <Box className={classes.actionButtons}>
                    <CustomButton
                        variant="greenFilled"
                        size="medium"
                        onClick={() => onFazerApontamento(
                            estimativa.user_id,
                            rotasimplesempresa,
                            estimativa.orcamento_id,
                            estimativa.id
                        )}
                    >
                        2 - Fazer Apontamento
                    </CustomButton>
                </Box>
            );
        } else if (status === 'aguardando equipamento') {
            return (
                <CustomButton
                    variant="darkBlueFilled"
                    size="medium"
                    onClick={() => onReceberEquipamento(estimativa.id)}
                    style={{ position: 'absolute', top: 10, right: 10 }}
                >
                    1 - Receber Equipamento
                </CustomButton>
            );
        } else if (status === 'autorizar manutencao') {
            return (
                <Box className={classes.actionButtons}>
                    <CustomButton
                        variant="greenFilled"
                        size="medium"
                        onClick={() => onApproveMaintenance && onApproveMaintenance(estimativa.id)}
                    >
                        Aprovar para Manutenção
                    </CustomButton>
                </Box>
            );
        } else if (status === 'pronto para retirada') {
            return (
                <Box className={classes.actionButtons}>
                    <CustomButton
                        variant="darkBlueFilled"
                        size="medium"
                        onClick={() => onMarkAsSent && onMarkAsSent(estimativa.id)}
                    >
                        Marcar como Enviado
                    </CustomButton>
                </Box>
            );
        } else if (status === 'enviado') {
            return (
                <Box style={{ position: 'absolute', top: 10, right: 10 }}>
                    <CustomButton
                        variant="darkBlueFilled"
                        size="medium"
                        onClick={onFinalizarFluxo}
                    >
                        Finalizar Operação
                    </CustomButton>
                </Box>
            );
        }

        return null;
    };

    // Client buttons: accept/refuse/shipping details
    renderClientButtons = () => {
        const {
            classes,
            estimativa,
            onAceitarEstimativa,
            onRecusarEstimativa,
            onVerDetalhesEnvio
        } = this.props;
        const status = estimativa?.status?.toLowerCase();

        // Status: aguardando equipamento - Show shipping details and cancel option
        if (status === 'aguardando equipamento') {
            return (
                <Box className={classes.clientActionButtons}>
                    <CustomButton
                        variant="blueFilled"
                        size="medium"
                        onClick={() => onVerDetalhesEnvio && onVerDetalhesEnvio(estimativa.id)}
                    >
                        Ver detalhes de envio
                    </CustomButton>
                    <CustomButton
                        variant="redFilled"
                        size="medium"
                        onClick={() => onRecusarEstimativa && onRecusarEstimativa(estimativa.id)}
                    >
                        Cancelar e Recusar
                    </CustomButton>
                </Box>
            );
        }

        // Status: estimativa entregue - Show accept/refuse buttons
        if (status === 'estimativa entregue') {
            return (
                <Box className={classes.clientActionButtons}>
                    <CustomButton
                        variant="greenFilled"
                        size="medium"
                        onClick={() => onAceitarEstimativa && onAceitarEstimativa(estimativa.id)}
                    >
                        Aceitar
                    </CustomButton>
                    <CustomButton
                        variant="redFilled"
                        size="medium"
                        onClick={() => onRecusarEstimativa && onRecusarEstimativa(estimativa.id)}
                    >
                        Recusar
                    </CustomButton>
                </Box>
            );
        }

        return null;
    };

    renderActionButtons = () => {
        const { modo } = this.props;

        // Client view: render client-specific buttons
        if (modo === 'cliente') {
            return this.renderClientButtons();
        }

        // Workshop/Admin view: render equipment workflow buttons
        return this.renderWorkshopAdminButtons();
    };

    render() {
        const { classes, estimativa, modo } = this.props;

        if (!estimativa) {
            return (
                <Typography className={classes.bodyText}>
                    Nenhuma estimativa carregada.
                </Typography>
            );
        }

        let itens = estimativa.itens;
        if (typeof itens === "string") {
            try {
                itens = JSON.parse(itens);
            } catch (e) {
                itens = [];
            }
        }

        let localizacaoObj = null;
        let isNewFormat = false;

        if (itens && !Array.isArray(itens) && typeof itens === 'object') {
            isNewFormat = true;
            localizacaoObj = itens.localizacao;
        }

        const enderecoExibicao = (isNewFormat && localizacaoObj)
            ? `${localizacaoObj.tipo || ''} - ${localizacaoObj.endereco || ''}`
            : estimativa.endereco || '-';

        return (
            <Box className={classes.sectionBody} position="relative">
                {/* Workshop/Admin buttons appear at top-right */}
                {modo !== 'cliente' && this.renderWorkshopAdminButtons()}

                <Typography className={classes.bodyText}>
                    <strong>N° Estimativa:</strong> {estimativa.id || '-'}
                </Typography>
                <Typography className={classes.bodyText} style={{ marginBottom: estimativa.motivo_recusa ? '0.2rem' : '1rem' }}>
                    <strong>Status:</strong> {estimativa.status}
                </Typography>
                {estimativa.motivo_recusa && (
                    <Typography className={classes.bodyText} style={{ color: '#d32f2f' }}>
                        <strong>Motivo da recusa:</strong> {estimativa.motivo_recusa}
                    </Typography>
                )}
                <Typography className={classes.bodyText}>
                    <strong>Local:</strong> {enderecoExibicao}
                </Typography>

                {isNewFormat ? (
                    <Box style={{ marginTop: 16 }}>
                        {itens.equipamento_info && (
                            <Box mb={2}>
                                <Typography className={classes.bodyText}>
                                    <strong>Equipamento Informado:</strong>
                                </Typography>
                                <Typography component="div" variant="body2" style={{ marginLeft: 16 }}>
                                    <div><strong>Marca:</strong> {itens.equipamento_info.marca || '-'}</div>
                                    <div><strong>Modelo:</strong> {itens.equipamento_info.modelo || '-'}</div>
                                    <div><strong>Problema Relatado:</strong> {itens.equipamento_info.problema || '-'}</div>
                                    <div><strong>Quantidade:</strong> {itens.equipamento_info.quantidade || '-'}</div>
                                </Typography>
                            </Box>
                        )}

                        {Array.isArray(itens.servicos) && itens.servicos.length > 0 && (
                            <Box mb={2}>
                                <Typography className={classes.bodyText}>
                                    <strong>Serviços:</strong>
                                </Typography>
                                <FlatList
                                    items={itens.servicos.map((item, index) => ({
                                        id: item.id || index,
                                        title: `${item.nome || item.servico || '-'} (Qtd: ${item.quantidade || 1}) - Unit: ${this.formatarMoeda(item.valorUnitario || item.valor)} / Total: ${this.formatarMoeda(item.valorTotal || item.valor)}`,
                                        description: item.obs || item.observacoes || itens.observacoes || 'Sem observações',
                                    }))}
                                />
                            </Box>
                        )}

                        {itens.apontamentos && (
                            <Box mb={2}>
                                <Typography className={classes.bodyText}>
                                    <strong>Apontamentos do técnico:</strong>
                                </Typography>
                                <Typography component="div" variant="body2" style={{ marginLeft: 16, marginTop: 4, whiteSpace: 'pre-line' }}>
                                    {itens.apontamentos}
                                </Typography>
                            </Box>
                        )}

                        {Array.isArray(itens.equipamentos) && itens.equipamentos.length > 0 && (
                            <Box mb={2}>
                                <Typography className={classes.bodyText}>
                                    <strong>Equipamentos Vinculados:</strong>
                                </Typography>
                                <List dense>
                                    {itens.equipamentos.map((eq, i) => (
                                        <ListItem key={i} style={{ paddingLeft: 16, paddingTop: 0, paddingBottom: 0 }}>
                                            <ListItemText primary={`- ${eq.nome || eq}`} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box>
                        <Typography className={classes.bodyText} style={{ marginTop: 16 }}>
                            <strong>Itens:</strong>
                        </Typography>
                        <FlatList
                            items={Array.isArray(itens) && itens.length > 0 ? (
                                itens.map((item, index) => ({
                                    id: item.id || index,
                                    title: item.servico || '-',
                                    description: item.obs || item.observacoes || itens.observacoes || 'Sem observações',
                                }))
                            ) : (
                                [{
                                    id: 'empty',
                                    title: 'Nenhum item',
                                    description: '',
                                }]
                            )}
                        />
                    </Box>
                )}

                {modo === 'admin' && this.props.adminResponseEditor ? (
                    (() => {
                        const isAguardandoEstimativa = estimativa?.status?.toLowerCase() === 'aguardando estimativa';
                        const isReadOnly = this.props.adminResponseEditor.fieldsReadOnly || !isAguardandoEstimativa;
                        return (
                            <Box className={classes.respostaBox}>
                                <TextField
                                    label="Resposta ao Cliente"
                                    value={this.props.adminResponseEditor.textoResposta}
                                    onChange={this.props.adminResponseEditor.onTextoRespostaChange}
                                    variant="outlined"
                                    multiline
                                    rows={4}
                                    fullWidth
                                    InputProps={{ readOnly: isReadOnly }}
                                    disabled={isReadOnly}
                                />
                                <Box mt={2}>
                                    <TextField
                                        label="Valor"
                                        value={this.props.adminResponseEditor.valorResposta}
                                        onChange={this.props.adminResponseEditor.onValorRespostaChange}
                                        variant="outlined"
                                        fullWidth
                                        type="number"
                                        inputProps={{ step: '0.01', min: '0' }}
                                        InputProps={{ readOnly: isReadOnly }}
                                        disabled={isReadOnly}
                                    />
                                </Box>
                                {this.props.adminResponseEditor.showResponderButton && isAguardandoEstimativa && (
                                    <Box className={classes.adminResponseActions}>
                                        <CustomButton
                                            variant="darkBlueFilled"
                                            size="medium"
                                            onClick={this.props.adminResponseEditor.onEnviarResposta}
                                            disabled={this.props.adminResponseEditor.responseSaving}
                                        >
                                            Responder Cliente
                                        </CustomButton>
                                    </Box>
                                )}
                            </Box>
                        );
                    })()
                ) : (
                    <Box className={classes.respostaBox}>
                        <strong>Resposta:</strong> {estimativa.texto_resposta || '-'}
                        <br />
                        <strong>Valor:</strong> {this.formatarMoeda(estimativa.valor_resposta)}
                    </Box>
                )}



                {/* Client buttons appear at bottom */}
                {modo === 'cliente' && this.renderClientButtons()}
            </Box>
        );
    }
}

export default withStyles(styles)(DetailsEstimativaSection);
